import React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';

import { jarvisApi } from '../api';
import { logger } from '../logging';

interface BlobFile {
  name: string;
  lastModified: Date;
  size: number;
}

interface ResultsTableProps {
  containerName: string;
}

type Order = 'asc' | 'desc';
type OrderBy = 'name' | 'lastModified' | 'size';

export const ResultsTable: React.FC<ResultsTableProps> = ({ containerName }) => {
  const [files, setFiles] = React.useState<BlobFile[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [order, setOrder] = React.useState<Order>('desc');
  const [orderBy, setOrderBy] = React.useState<OrderBy>('lastModified');
  const [downloadingFile, setDownloadingFile] = React.useState<string | null>(null);

  const fetchFiles = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await jarvisApi.listResults(containerName);
      setFiles(response || []);
    } catch (e: any) {
      logger.error('Failed to fetch results', e);
      setError('Failed to load results. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [containerName]);

  React.useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleDownload = async (blobName: string) => {
    if (downloadingFile) return;
    setDownloadingFile(blobName);
    try {
      await jarvisApi.downloadResult(containerName, blobName);
      logger.info(`Download Success: ${blobName}`);
    } catch (e: any) {
      logger.error(`Download Error: ${blobName}`, { error: { message: e.message, stack: e.stack } });
    } finally {
      setDownloadingFile(null);
    }
  };

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleString();
  };

  const highlightText = (text: string, highlight: string) => {
    if (!highlight.trim()) {
      return text;
    }

    const regex = new RegExp(`(${highlight})`, 'gi');
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <span
              key={index}
              style={{
                backgroundColor: '#ffeb3b',
                color: 'rgba(0, 0, 0, 0.87)',
                fontWeight: 600,
                padding: '0 2px',
              }}
            >
              {part}
            </span>
          ) : (
            part
          )
        )}
      </>
    );
  };

  const filteredFiles = files.filter((file) =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedFiles = React.useMemo(() => {
    const comparator = (a: BlobFile, b: BlobFile) => {
      let aValue: any = a[orderBy];
      let bValue: any = b[orderBy];

      if (orderBy === 'lastModified') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (bValue < aValue) {
        return order === 'asc' ? 1 : -1;
      }
      if (bValue > aValue) {
        return order === 'asc' ? -1 : 1;
      }
      return 0;
    };

    return [...filteredFiles].sort(comparator);
  }, [filteredFiles, order, orderBy]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          label="Search files..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <IconButton onClick={fetchFiles} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {sortedFiles.length === 0 ? (
        <Typography sx={{ p: 4, textAlign: 'center' }} color="text.secondary">
          No files found
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'lastModified'}
                    direction={orderBy === 'lastModified' ? order : 'asc'}
                    onClick={() => handleRequestSort('lastModified')}
                  >
                    Last Modified
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'size'}
                    direction={orderBy === 'size' ? order : 'asc'}
                    onClick={() => handleRequestSort('size')}
                  >
                    Size
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedFiles.map((file) => (
                <TableRow key={file.name} hover>
                  <TableCell>{highlightText(file.name, searchTerm)}</TableCell>
                  <TableCell>{formatDate(file.lastModified)}</TableCell>
                  <TableCell>{formatFileSize(file.size)}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => handleDownload(file.name)}
                      color="primary"
                      size="small"
                      disabled={!!downloadingFile}
                    >
                      {downloadingFile === file.name
                        ? <CircularProgress size={18} color="inherit" />
                        : <DownloadIcon />}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
